/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getCachedUserToken } from '../../../utilities/caching';
import { gotoUrlEnvironment } from '../../../utilities/helpers';
import { ACCOUNT_REGIONS, VerificationState, TIER_LEVEL } from '../../../utilities/constants';
import { showAlert } from '../../../utilities/notifications';
import {
  SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY,
  USER_REGION_SETTINGS_QUERY_KEY,
} from '../../../utilities/sharedQueries';
import { useUserPersonalInfo } from '../../../utilities/sharedHooks/user';
import useRiskTierSettings from '../../../utilities/sharedHooks/useRiskTierSettings';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import { ORDERABLE_CARD_TYPES } from '../queries';
import { mainCardTypes } from '../constants';
import {
  CARD_ORDER_DEEPLINK_LEGACY_PATH,
  CARD_ORDER_DEEPLINK_QUERY_PARAMS,
  CARD_ORDER_DEEPLINK_SESSION_KEYS,
} from './constants';

const CardOrderDeeplink = () => {
  const location = useLocation();
  const history = useHistory();
  const { t: translate } = useTranslation();

  const { data: supportedCountries, isLoading: isLoadingSupportedCountries } = useQuery(
    SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY
  );
  const { data: userRegionSettings, isLoading: isLoadingRegionSettings } = useQuery(
    USER_REGION_SETTINGS_QUERY_KEY
  );
  const { data: cardOptionsData, isLoading: isLoadingCardOptions } = useQuery(ORDERABLE_CARD_TYPES);
  const [settings, isLoadingSettings] = useSetting('server_configurations', [
    'card_support_email_address',
  ]);
  const { verificationState, countryId, isLoading: isLoadingPersonalInfo } = useUserPersonalInfo();
  const { riskAssessment, isLoading: isLoadingRiskAssessment } = useRiskTierSettings();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const appId = searchParams.get(CARD_ORDER_DEEPLINK_QUERY_PARAMS.APP_ID);
    const pathname = location.pathname.replace(/\/+$/, '');
    const isLegacyPhysicalPath = pathname === CARD_ORDER_DEEPLINK_LEGACY_PATH;

    const isRowApp = appId === process.env.REACT_APP_ROW_APP_NAME;
    const isUsApp = appId === process.env.REACT_APP_US_APP_NAME;
    const isValidAppId = isRowApp || isUsApp;
    const isOnRowHost = window.location.hostname === process.env.REACT_APP_FE_BASE_HOSTNAME;
    const isOnUsHost = window.location.hostname === process.env.REACT_APP_US_FE_BASE_HOSTNAME;

    // client always enters through the ROW base URL; hop to the US app unless
    // we're already on the app that matches the given app_id. Without a
    // recognized app_id there's nothing to match against, so stay on
    // whichever app is already open.
    if (isValidAppId && !(isRowApp && isOnRowHost) && !isOnUsHost) {
      gotoUrlEnvironment(ACCOUNT_REGIONS.US, `${pathname.slice(1)}${location.search}`);
      return;
    }

    // Remembered before anything else: a token that's already expired makes the
    // requests below answer 401, which resets the session and hard-redirects to
    // /timeout, so this is the only chance to record where to come back to.
    sessionStorage.setItem(
      CARD_ORDER_DEEPLINK_SESSION_KEYS.RETURN_URL,
      `${pathname}${location.search}`
    );

    const token = getCachedUserToken();
    if (!token) {
      history.push('/onboarding/login');
      return;
    }

    if (
      isLoadingSupportedCountries ||
      isLoadingRegionSettings ||
      isLoadingCardOptions ||
      isLoadingSettings ||
      isLoadingRiskAssessment ||
      isLoadingPersonalInfo
    ) {
      return;
    }

    const userCountry = supportedCountries?.find((country) => country.id === countryId);
    const isCardOrderSupported =
      userCountry?.can_order_cards && !userCountry.is_sanctioned && userCountry.risk_level < 2;

    const orderableCardTypes = [
      ...new Set(
        (cardOptionsData?.orderableCardTypes || [])
          .flatMap((provider) => provider.cards || [])
          .map((card) => card.type)
          .filter(Boolean)
      ),
    ];
    const isAnyCardTypeOrderable = orderableCardTypes.length > 0;
    const isPhysicalOrderable = orderableCardTypes.includes(mainCardTypes.PHYSICAL.toUpperCase());

    const { key: userTier, order_card: allowOrderCard } = riskAssessment?.userRisk || {};

    if (userTier === TIER_LEVEL.ZERO) {
      history.push(`/kyc`, { switch: true });
      return;
    }

    if (userTier === TIER_LEVEL.FOUR && !allowOrderCard) {
      history.push('/dashboard/settings/account', { upgradeAccount: true });
      return;
    }

    const isOrderCardAllowed =
      userRegionSettings?.can_order_card &&
      verificationState !== VerificationState.PENDING &&
      verificationState !== VerificationState.CHECKED &&
      (riskAssessment?.userRisk?.key !== TIER_LEVEL.FOUR ||
        (riskAssessment?.userRisk?.key === TIER_LEVEL.FOUR &&
          riskAssessment?.userRisk?.order_card) ||
        (riskAssessment?.userRisk?.key === TIER_LEVEL.FOUR &&
          !riskAssessment?.userRisk?.order_card &&
          !riskAssessment?.userNextTier?.hasActiveChangeTierRequest?.id));

    if (
      !isCardOrderSupported ||
      !isAnyCardTypeOrderable ||
      !isOrderCardAllowed ||
      (isLegacyPhysicalPath && !isPhysicalOrderable)
    ) {
      showAlert({
        message: translate('feature_not_supported_in_area').replace(
          '%s',
          `<a class="link" href="mailto:${settings?.card_support_email_address}">${settings?.card_support_email_address}</a>`
        ),
        type: 'danger',
      });
      history.push('/dashboard/card');
      return;
    }

    // the deeplink made it through, so it shouldn't be replayed on a later login
    sessionStorage.removeItem(CARD_ORDER_DEEPLINK_SESSION_KEYS.RETURN_URL);
    history.push(
      '/dashboard/card/order-card',
      isLegacyPhysicalPath ? { mainCardType: mainCardTypes.PHYSICAL } : undefined
    );
  }, [
    countryId,
    supportedCountries,
    userRegionSettings,
    cardOptionsData,
    settings,
    verificationState,
    riskAssessment,
    isLoadingSupportedCountries,
    isLoadingRegionSettings,
    isLoadingCardOptions,
    isLoadingSettings,
    isLoadingRiskAssessment,
    isLoadingPersonalInfo,
  ]);

  return null;
};

export default CardOrderDeeplink;
